// pages/dataAnalyst/performance/performanceFormat.js
//
// Pure helpers for the "My Performance" KRA/KPI page: April-March fiscal
// year months, and display formatting for KPI values/statuses. No data is
// derived here -- every number shown comes from GET /performance/my-kra-kpi.

export const FY_MONTHS = [
  { num: 4, label: "April" }, { num: 5, label: "May" }, { num: 6, label: "June" },
  { num: 7, label: "July" }, { num: 8, label: "August" }, { num: 9, label: "September" },
  { num: 10, label: "October" }, { num: 11, label: "November" }, { num: 12, label: "December" },
  { num: 1, label: "January" }, { num: 2, label: "February" }, { num: 3, label: "March" },
];

const pad2 = (n) => String(n).padStart(2, "0");

export function fiscalYearLabelFor(date = new Date()) {
  const start = date.getMonth() + 1 >= 4 ? date.getFullYear() : date.getFullYear() - 1;
  return `${start}-${pad2((start + 1) % 100)}`;
}

// ("2026-27", 4) -> "2026-04"; ("2026-27", 1) -> "2027-01"
export function monthKeyFor(fyLabel, monthNum) {
  const start = Number(String(fyLabel).slice(0, 4));
  const year = monthNum >= 4 ? start : start + 1;
  return `${year}-${pad2(monthNum)}`;
}

export function monthsForFiscalYear(fyLabel, now = new Date()) {
  const current = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
  return FY_MONTHS.map((m) => {
    const key = monthKeyFor(fyLabel, m.num);
    return { ...m, key, isFuture: key > current };
  });
}

// Default selection: current FY + current month; for a past FY, March.
export function defaultMonthFor(fyLabel, now = new Date()) {
  const months = monthsForFiscalYear(fyLabel, now);
  const available = months.filter((m) => !m.isFuture);
  return (available[available.length - 1] || months[0]).num;
}

export function formatMonthKey(key) {
  if (!key) return "—";
  const [y, m] = String(key).split("-").map(Number);
  const month = FY_MONTHS.find((x) => x.num === m);
  return month ? `${month.label} ${y}` : key;
}

export const CALCULATION_LABELS = {
  RATIO_HIGHER_BETTER: "Ratio (higher is better)",
  RATIO_LOWER_BETTER: "Ratio (lower is better)",
  AVERAGE_SCORE: "Average score",
  COUNT_CUMULATIVE: "Cumulative count",
  RATING_SCALE: "Rating scale",
  FORM_SCORE: "Form score",
  MANUAL: "Manual rating",
};

export const STATUS_META = {
  ON_TRACK: { label: "On Track", tone: "green" },
  AT_RISK: { label: "At Risk", tone: "red" },
  NO_DATA: { label: "No Data", tone: "gray" },
  MANUAL_ENTRY_REQUIRED: { label: "Manual Entry Required", tone: "amber" },
  AWAITING_REVIEW: { label: "Awaiting Review", tone: "blue" },
  CHANGES_REQUESTED: { label: "Changes Requested", tone: "amber" },
  MANUAL_RATING_REQUIRED: { label: "Manual Rating Required", tone: "purple" },
  NOT_APPLICABLE: { label: "Not Applicable", tone: "gray" },
};

// Manual evidence state (API evidence_status). Only APPROVED entries count
// toward the score.
export const EVIDENCE_STATUS_META = {
  NOT_SUBMITTED: { label: "Not submitted", tone: "gray" },
  DRAFT: { label: "Draft", tone: "gray" },
  AWAITING_REVIEW: { label: "Submitted · Awaiting Review", tone: "blue" },
  CHANGES_REQUESTED: { label: "Changes Requested", tone: "amber" },
  APPROVED: { label: "Approved", tone: "green" },
  REJECTED: { label: "Rejected", tone: "red" },
};

export const DATA_SOURCE_META = {
  AUTOMATIC: { label: "Automatic", tone: "teal" },
  MANUAL: { label: "Manual", tone: "amber" },
  MIXED: { label: "Mixed", tone: "purple" },
};

const isNil = (v) => v === null || v === undefined;

function trimNumber(v, digits = 2) {
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(digits).replace(/0+$/, "").replace(/\.$/, "");
}

// A KPI result: "93.64%" / "3" / "4.2" -- or "No data" (never 0 for missing).
export function formatKpiValue(calculationType, result) {
  if (!result || isNil(result.value)) return "No data";
  if (calculationType === "RATIO_HIGHER_BETTER" || calculationType === "RATIO_LOWER_BETTER") return `${trimNumber(result.value)}%`;
  return trimNumber(result.value);
}

// "103 / 110" under a ratio value; null otherwise.
export function formatRatioParts(calculationType, result) {
  if (!result || !String(calculationType).startsWith("RATIO")) return null;
  if (isNil(result.numerator) || isNil(result.denominator)) return null;
  return `${trimNumber(result.numerator)} / ${trimNumber(result.denominator)}`;
}

export function formatScore(v, digits = 2) {
  return isNil(v) ? "—" : trimNumber(v, digits);
}

// Phase 65: monthly User Feedback state (API kpi.feedback.month.status).
// AWAITING_FEEDBACK is never shown as a 0 score.
export const FEEDBACK_STATUS_META = {
  NOT_REQUESTED: { label: "Feedback not requested", tone: "gray" },
  REQUEST_FAILED: { label: "Feedback request failed", tone: "red" },
  AWAITING_FEEDBACK: { label: "Awaiting User Feedback", tone: "blue" },
  RECEIVED: { label: "Feedback received", tone: "green" },
};

// "4.6 / 5" -- or null when there is no average yet (never "0 / 5").
export function formatFeedbackAverage(average, maxScore) {
  if (isNil(average)) return null;
  return isNil(maxScore) ? trimNumber(average) : `${trimNumber(average)} / ${trimNumber(maxScore)}`;
}
